import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import {
  useGetAppealTemplatesQuery,
  useUpsertAppealTemplateMutation,
} from "../../store/queries/claims";

const NEW_TEMPLATE = "__new__";

const Container = styled.div`
  max-width: 820px;
  padding: 1rem 0;
`;

const Field = styled.div`
  margin-bottom: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
`;

const Hint = styled.div`
  font-size: 0.8rem;
  color: #6c757d;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

/**
 * AppealTemplateEditor — CLE-2702
 *
 * SuperAdmin editor for the per-classification bulk appeal email templates used by
 * the Bulk Appeals dashboard. Pick an existing template (keyed by denial
 * classification) to edit, or add a new one. The subject/body may use the tokens
 * {{payer_name}}, {{claim_count}}, {{total_expected}}, {{total_paid}},
 * {{total_underpayment}} and {{claim_table}} (the claim table + totals are appended
 * automatically if {{claim_table}} is omitted).
 */
function AppealTemplateEditor() {
  const toastRef = React.useRef<Toast>(null);
  const { data: templates = [], isLoading } = useGetAppealTemplatesQuery();
  const [upsertAppealTemplate, { isLoading: isSaving }] =
    useUpsertAppealTemplateMutation();

  const [selected, setSelected] = useState<string>("");
  const [newClassification, setNewClassification] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const options = useMemo(
    () => [
      ...templates.map((template) => ({
        label: template.classification,
        value: template.classification,
      })),
      { label: "+ New template", value: NEW_TEMPLATE },
    ],
    [templates]
  );

  // Default to the first template once loaded.
  useEffect(() => {
    if (!selected && templates.length > 0) {
      setSelected(templates[0].classification);
    }
  }, [templates, selected]);

  // Load the selected template's content (or clear for a new one).
  useEffect(() => {
    if (selected === NEW_TEMPLATE) {
      setNewClassification("");
      setSubject("");
      setBody("");
      return;
    }
    const template = templates.find((t) => t.classification === selected);
    if (template) {
      setSubject(template.subject);
      setBody(template.body);
    }
  }, [selected, templates]);

  const isNew = selected === NEW_TEMPLATE;
  const classification = isNew ? newClassification.trim() : selected;

  const handleSave = async () => {
    if (!classification || !subject.trim() || !body.trim()) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Missing fields",
        detail: "Classification, subject and body are all required.",
        life: 4000,
      });
      return;
    }

    try {
      await upsertAppealTemplate({
        classification,
        subject: subject.trim(),
        body: body.trim(),
      }).unwrap();

      toastRef.current?.show({
        severity: "success",
        summary: "Saved",
        detail: `Template for "${classification}" saved.`,
        life: 3000,
      });

      if (isNew) {
        setSelected(classification);
      }
    } catch {
      toastRef.current?.show({
        severity: "error",
        summary: "Save failed",
        detail: "Could not save the appeal template.",
        life: 5000,
      });
    }
  };

  if (isLoading) {
    return <ProgressSpinner />;
  }

  return (
    <Container>
      <Toast ref={toastRef} />

      <Message
        severity="info"
        text="Templates are keyed by denial classification and used to assemble bulk appeal letters. Available tokens: {{payer_name}}, {{claim_count}}, {{total_expected}}, {{total_paid}}, {{total_underpayment}}, {{claim_table}}."
        style={{ marginBottom: "1.25rem", width: "100%" }}
      />

      <Field>
        <Label htmlFor="appeal-template-select">Template</Label>
        <Dropdown
          inputId="appeal-template-select"
          value={selected}
          options={options}
          onChange={(e) => setSelected(e.value)}
          placeholder="Select a template"
        />
      </Field>

      {isNew && (
        <Field>
          <Label htmlFor="appeal-template-classification">
            Denial classification
          </Label>
          <InputText
            id="appeal-template-classification"
            value={newClassification}
            onChange={(e) => setNewClassification(e.target.value)}
            placeholder="Exact denial classification title (e.g. Prior Authorization Failure)"
          />
          <Hint>
            Must match the claim&apos;s denial classification title exactly.
          </Hint>
        </Field>
      )}

      <Field>
        <Label htmlFor="appeal-template-subject">Subject</Label>
        <InputText
          id="appeal-template-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Request for Reprocessing - ..."
        />
      </Field>

      <Field>
        <Label htmlFor="appeal-template-body">Body</Label>
        <InputTextarea
          id="appeal-template-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={16}
          autoResize
        />
      </Field>

      <Actions>
        <Button
          label={isNew ? "Create Template" : "Save Template"}
          icon="pi pi-save"
          loading={isSaving}
          onClick={handleSave}
        />
      </Actions>
    </Container>
  );
}

export default AppealTemplateEditor;
